import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

interface TooltipProps {
  /** Either a string or a richer React node. */
  content: ReactNode;
  /** Tooltip placement preference. */
  side?: 'top' | 'bottom';
  /** Delay before showing in ms. */
  delay?: number;
  /** Max width of the tooltip in px. */
  maxWidth?: number;
  children: ReactElement;
}

/**
 * Lightweight portal-positioned tooltip. Escapes overflow:hidden containers
 * (important for table cells). Use the <Term> wrapper for glossary terms.
 */
export function Tooltip({
  content,
  side = 'top',
  delay = 220,
  maxWidth = 280,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; place: 'top' | 'bottom' } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  function show(target: HTMLElement) {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const rect = target.getBoundingClientRect();
      const wantTop = side === 'top';
      const fitsTop = rect.top > 80;
      const place = wantTop ? (fitsTop ? 'top' : 'bottom') : (rect.bottom < window.innerHeight - 80 ? 'bottom' : 'top');
      const y = place === 'top' ? rect.top - 8 : rect.bottom + 8;
      const x = Math.min(window.innerWidth - 12, Math.max(12, rect.left + rect.width / 2));
      setPos({ x, y, place });
      setOpen(true);
    }, delay);
  }

  function hide() {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(false);
  }

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!isValidElement(children)) return children;

  const trigger = cloneElement(children as ReactElement<any>, {
    ref: (el: HTMLElement | null) => {
      triggerRef.current = el;
      const childRef = (children as any).ref;
      if (typeof childRef === 'function') childRef(el);
      else if (childRef && typeof childRef === 'object') childRef.current = el;
    },
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      show(e.currentTarget);
      (children.props as any).onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      hide();
      (children.props as any).onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      show(e.currentTarget);
      (children.props as any).onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      hide();
      (children.props as any).onBlur?.(e);
    },
    'aria-describedby': open ? id : undefined,
  });

  return (
    <>
      {trigger}
      {open && pos && createPortal(
        <div
          id={id}
          role="tooltip"
          className={cn(
            'fixed z-[200] -translate-x-1/2 pointer-events-none animate-fade-in',
            pos.place === 'top' ? '-translate-y-full' : '',
          )}
          style={{ left: pos.x, top: pos.y, maxWidth }}
        >
          <div className="bg-fg text-bg-elevated rounded-md px-2.5 py-1.5 shadow-pop text-xs leading-snug">
            {content}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
